import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Tooltip estándar para explicaciones (reemplaza al `title` nativo, que tarda en salir y no se
 * puede estilizar). Sale a los 150 ms, con ancho y alto acotados: un texto largo hace scroll
 * dentro del globo en vez de taparle la pantalla a quien lo lee.
 *
 * - Con `children`: el hijo es el disparador (`asChild`), sin envolverlo en otro elemento.
 *   El hijo debe aceptar ref y props de evento (un elemento DOM o un componente con forwardRef).
 * - Sin `children`: pinta un ícono de información chico, enfocable con teclado, como disparador.
 *
 * Sale con hover o con un toque en táctil; el foco no lo abre (al abrir un popover salía solo).
 *
 * Requiere un `TooltipProvider` arriba (ya está en App.tsx).
 */
export const INFO_TOOLTIP_CONTENT_CLS =
  "max-w-xs max-h-60 overflow-y-auto overflow-x-hidden text-[12px] leading-snug whitespace-pre-line break-words";

export interface InfoTooltipProps {
  /** Texto (o nodo) de la explicación. Si viene vacío, se pinta solo el hijo, sin tooltip. */
  content: ReactNode;
  children?: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Clases extra del ícono cuando no hay `children`. */
  iconClassName?: string;
  /** Etiqueta accesible del ícono cuando no hay `children`. */
  ariaLabel?: string;
  className?: string;
}

export function InfoTooltip({
  content,
  children,
  side = "top",
  align = "center",
  iconClassName,
  ariaLabel = "Más información",
  className,
}: InfoTooltipProps) {
  if (content == null || content === "" || content === false) return <>{children}</>;
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild abrirAlTocar>
        {children ?? (
          // span (no button): puede vivir dentro de una tarjeta clickeable sin anidar botones.
          // El click no se propaga, así tocar el ícono no dispara la acción de la tarjeta.
          <span
            role="img"
            tabIndex={0}
            aria-label={ariaLabel}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            className="inline-flex shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info className={cn("h-3 w-3", iconClassName)} strokeWidth={2} />
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent side={side} align={align} collisionPadding={12} className={cn(INFO_TOOLTIP_CONTENT_CLS, className)}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
